/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import ModalManager, { ariaHidden } from "./ModalManager";
import { IPortalProps, Portal } from "../Portal";
import { useEventCallback, useForkRef } from "../../../hooks";
import { overrideForwardRef } from "../OverridableComponent";
import { createChainedFunction, getOwnerDocument } from "../../../utils";

export interface IModalBaseProps {
	BackdropComponent?: React.ElementType;
	children: React.ReactElement;
	closeAfterTransition?: boolean;
	container?: IPortalProps["container"];
	disableAutoFocus?: boolean;
	disableEnforceFocus?: boolean;
	disableEscapeKeyDown?: boolean;
	disablePortal?: IPortalProps["disablePortal"];
	disableRestoreFocus?: boolean;
	disableScrollLock?: boolean;
	hideBackdrop?: boolean;
	keepMounted?: boolean;
	onBackdropClick?: React.ReactEventHandler;
	onClose?: {
		bivarianceHack(event: Record<string, unknown>, reason: "backdropClick" | "escapeKeyDown"): void;
	}["bivarianceHack"];
	open: boolean;
	manager?: ModalManager;
	className?: string;
}

function getContainer(container: IModalBaseProps["container"]): Element {
	return isFunction(container) ? container() : container;
}

function getHasTransition(children: React.ReactNode): boolean {
	return children ? Object.prototype.hasOwnProperty.call(children["props"], "in") : false;
}

const defaultModalManager = new ModalManager();

export const ModalBase = overrideForwardRef<IModalBaseProps, "div">(
	(
		{
			Component,
			BackdropComponent,
			children,
			className,
			closeAfterTransition = false,
			container,
			disableAutoFocus = false,
			disableEnforceFocus = false,
			disableEscapeKeyDown = false,
			disablePortal = false,
			disableRestoreFocus = false,
			disableScrollLock = false,
			hideBackdrop = false,
			keepMounted = false,
			manager,
			onBackdropClick,
			onClose,
			open,
			onKeyDown,
			onTransitionEnter,
			onTransitionExited,
			...restProps
		},
		ref
	) => {
		const [ exited, setExited ] = useState(true);
		const modal = useRef<{ modalRef?: HTMLElement; mountNode?: HTMLElement }>({});
		const mountNodeRef = useRef(null);
		const modalRef = useRef<HTMLElement>(null);
		const handleRef = useForkRef(modalRef, ref);
		const hasTransition = getHasTransition(children);

		const baseClass = BEM("edt-modal-base");

		const getDoc = () => getOwnerDocument(mountNodeRef.current);
		const getModal = () => {
			modal.current.modalRef = modalRef.current;
			modal.current.mountNode = mountNodeRef.current;
			return modal.current;
		};

		const handleMounted = () => {
			manager.mount(getModal(), { disableScrollLock });

			// Fix a bug on Chrome where the scroll isn't initially 0.
			modalRef.current.scrollTop = 0;
		};

		const handleOpen = useEventCallback(() => {
			const resolvedContainer = getContainer(container) || getDoc().body;

			manager.add(getModal(), resolvedContainer);

			// The element was already mounted.
			if (modalRef.current) {
				handleMounted();
			}
		});

		const isTopModal = useCallback(() => manager.isTopModal(getModal()), [ manager ]);

		const handlePortalRef = useEventCallback((node) => {
			mountNodeRef.current = node;

			if (!node) {
				return;
			}

			if (open && isTopModal()) {
				handleMounted();
			} else {
				ariaHidden(modalRef.current, true);
			}
		});

		const handleClose = useCallback(() => {
			manager.remove(getModal());
		}, [ manager ]);

		useEffect(() => {
			return () => {
				handleClose();
			};
		}, [ handleClose ]);

		useEffect(() => {
			if (open) {
				handleOpen();
			} else if (!hasTransition || !closeAfterTransition) {
				handleClose();
			}
		}, [ open, handleClose, hasTransition, closeAfterTransition, handleOpen ]);

		if (!keepMounted && !open && (!hasTransition || exited)) {
			return null;
		}

		const handleEnter = () => {
			setExited(false);

			if (isFunction(onTransitionEnter)) {
				onTransitionEnter();
			}
		};

		const handleExited = () => {
			setExited(true);

			if (isFunction(onTransitionExited)) {
				onTransitionExited();
			}

			if (closeAfterTransition) {
				handleClose();
			}
		};

		const handleBackdropClick = (event) => {
			if (event.target !== event.currentTarget) {
				return;
			}

			if (isFunction(onBackdropClick)) {
				onBackdropClick(event);
			}

			if (isFunction(onClose)) {
				onClose(event, "backdropClick");
			}
		};

		const handleKeyDown = (event) => {
			if (isFunction(onKeyDown)) {
				onKeyDown(event);
			}

			// The handler doesn't take event.defaultPrevented into account:
			//
			// event.preventDefault() is meant to stop default behaviors like
			// clicking a checkbox to check it, hitting a button to submit a form,
			// and hitting left arrow to move the cursor in a text input etc.
			// Only special HTML elements have these default behaviors.
			if (event.key !== "Escape" || !isTopModal()) {
				return;
			}

			if (!disableEscapeKeyDown) {
				// Swallow the event, in case someone is listening for the escape key on the body.
				event.stopPropagation();

				if (onClose) {
					onClose(event, "escapeKeyDown");
				}
			}
		};

		const childProps: Record<string, unknown> = {};

		if (children.props.tabIndex === undefined) {
			childProps.tabIndex = "-1";
		}

		// It's a Transition like component
		if (hasTransition) {
			childProps.onEnter = createChainedFunction(handleEnter, children.props.onEnter);
			childProps.onExited = createChainedFunction(handleExited, children.props.onExited);
		}

		if (className) {
			baseClass.add(className);
		}

		return (
			<Portal ref={handlePortalRef} container={container} disablePortal={disablePortal}>
				<Component
					className={baseClass.toClassName()}
					role="presentation"
					{...restProps}
					ref={handleRef}
					onKeyDown={handleKeyDown}
				>
					{!hideBackdrop && BackdropComponent ? (
						<BackdropComponent aria-hidden open={open} onClick={handleBackdropClick} />
					) : null}
					{React.cloneElement(children, childProps)}
				</Component>
			</Portal>
		);
	}
);

ModalBase.defaultProps = {
	Component: "div",
	manager: defaultModalManager
};
